import { makeStyles, Typography } from "@material-ui/core"

import { OptionProps, Selected } from "../../../../utils/safetyPlanUtils"
import { colors } from "../../../../utils/theme"

/**
 * Renders a Yes/No segmented pill toggle for a binary safety plan option.
 * Styled to match the read-only radio display in `SafetyPlanSelectedOptionValue`.
 * "Yes" is highlighted when the option has a stored value; "No" is highlighted
 * when no selection exists. Clicking either segment invokes `onClick` with an
 * empty value array, leaving the toggling logic to the parent `clickHandler`.
 *
 * @param option    - The `ResponseOption` whose id is used to look up the current selection.
 * @param onClick   - Callback invoked with `(id, [], type)` when either segment is clicked.
 * @param type      - The category type string passed through to `onClick`.
 * @param selection - The current list of selected items, used to determine active state.
 */
export default function RadioOption({
  option,
  onClick,
  type,
  selection,
}: OptionProps) {
  const el = selection.find((el: Selected) => el.id === option.id)
  const isYes = !!el?.value

  const classes = useStyles()

  return (
    <div className={classes.radioContainer}>
      <div
        role="group"
        aria-label="Keeping Myself Safe"
        className={classes.radioOptions}
      >
        <button
          aria-pressed={isYes}
          className={isYes ? classes.radioOptionSelected : classes.radioOption}
          onClick={() => onClick(option.id, [], type)}
        >
          <Typography className={classes.radioTxt}>Yes</Typography>
        </button>
        <button
          aria-pressed={!isYes}
          className={!isYes ? classes.radioOptionSelected : classes.radioOption}
          onClick={() => onClick(option.id, [], type)}
        >
          <Typography className={classes.radioTxt}>No</Typography>
        </button>
      </div>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  radioContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    borderRadius: "8px",
  },
  radioOptions: {
    display: "flex",
    height: "38px",
    padding: "2px",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "360px",
    background: "rgba(120, 120, 128, 0.12)",
  },
  radioOption: {
    display: "flex",
    height: "34px",
    padding: "3px 10px",
    alignItems: "center",
    flex: "1 0 0",
    borderRadius: "360px",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    background: "transparent",
  },
  radioOptionSelected: {
    display: "flex",
    height: "34px",
    padding: "3px 10px",
    alignItems: "center",
    flex: "1 0 0",
    borderRadius: "360px",
    justifyContent: "center",
    background: colors.neutral.white,
    cursor: "pointer",
    border: "none",
  },
  radioTxt: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    color: colors.safetyPlan.midnight,
  },
}))
